/**
 * Вывод реферального заработка (идея #162).
 *
 * Клиент просит вывести то, что заработал по рефералке. Деньги переводит сам партнёр
 * (карта, СБП, USDT): сначала жмёт «Перевожу» (заявка уходит в `processing`), потом
 * «Выплачено». Пока заявка открыта, сумма отложена с баланса. Отменить её клиент может
 * только до «Перевожу»: иначе он получил бы и перевод, и сумму обратно на баланс.
 * Открытая заявка одна.
 *
 * GRAM и CryptoBot (заявки #183, #186) может заплатить сервис автоматически, с комиссией
 * сети из суммы: сколько придёт «на руки», считает бэк (`/quote`) до отправки заявки.
 *
 * Та же логика есть в боте (раздел «👥 Рефералка»), тексты держим в одном тоне с ним:
 * деньги переводит «владелец сервиса», слова «партнёр» клиент не знает.
 */
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Coins,
  CreditCard,
  ExternalLink,
  Gem,
  Send,
  Smartphone,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import {
  api,
  ApiError,
  type CreateWithdrawalBody,
  type WithdrawQuote,
  type WithdrawalItem,
  type WithdrawMethod,
  type WithdrawState,
} from '@/lib/api';
import { IconChip, OptionRow, SectionLabel } from '@/components/ui/calm';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { confirmAction, getWebApp, haptic, hideKeyboard, openExternalUrl } from '@/lib/telegram';
import { currencySign, formatWholeMinor } from '@/lib/currency';
import { shortDateTime } from '@/lib/date';
import { cn } from '@/lib/utils';
import {
  BANK_MAX,
  MEMO_MAX,
  WITHDRAW_QUERY_KEY,
  canCancelWithdrawal,
  cardDigits,
  groupCard,
  isPlatformMethod,
  isValidBank,
  isValidCard,
  isValidTonAddress,
  isValidTrc20,
  normalizeBank,
  normalizeMemo,
  normalizePhone,
  withdrawErrorOf,
  withdrawMethodLabel,
  withdrawStatusLabel,
} from '@/lib/withdraw';

export const Route = createFileRoute('/referral-withdraw')({
  component: ReferralWithdraw,
});

const METHOD_ICON: Record<WithdrawMethod, LucideIcon> = {
  card: CreditCard,
  sbp: Smartphone,
  usdt_trc20: Coins,
  ton: Gem,
  cryptobot: Send,
};

/** Реквизиты в человеческом виде: у чека CryptoBot это Telegram клиента, а не его id. */
function shownDestination(item: WithdrawalItem): string {
  if (item.method === 'cryptobot') return i18n.t('Withdraw to telegram');
  return item.destination;
}

/** 5xx и обрыв связи показывает корневой обработчик своим тостом, второй не нужен. */
function isServerFailure(e: unknown): boolean {
  return e instanceof ApiError && (e.status >= 500 || e.status === 0);
}

function ReferralWithdraw() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const { data, isPending, refetch, isRefetching } = useQuery({
    queryKey: WITHDRAW_QUERY_KEY,
    queryFn: () => api.referralWithdrawState(),
    // Статус заявки меняется на стороне партнёра: из кэша прошлой сессии «ждёт»
    // висело бы у заявки, которую уже выплатили.
    refetchOnMount: 'always',
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    // Сюда приходят и с главной, и из профиля: возвращаем туда, откуда пришли.
    const onBack = () => {
      if (router.history.canGoBack()) router.history.back();
      else navigate({ to: '/me' });
    };
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate, router]);

  return (
    <div className="p-4 space-y-5 pb-24">
      <div>
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] mb-2 text-foreground">
          {t('Withdraw title')}
        </h2>
        <p className="text-sm text-[#8a8c93] leading-snug">{t('Withdraw manual note')}</p>
      </div>

      {isPending ? (
        <ReferralWithdrawSkeleton />
      ) : !data ? (
        <div className="calm-card rounded-[24px] p-6 text-center space-y-3 flex flex-col items-center">
          <AlertTriangle className="w-7 h-7 text-[#f0656a]" />
          <div className="text-sm text-[#8a8c93]">{t('Withdraw load failed')}</div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="calm-cta px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-60"
          >
            {t('Retry')}
          </button>
        </div>
      ) : (
        <WithdrawBody state={data} />
      )}
    </div>
  );
}

function WithdrawBody({ state }: { state: WithdrawState }) {
  const { t } = useTranslation();
  const open = state.open;
  // Открытая заявка есть и в истории: там она дублировала бы карточку сверху.
  const past = state.history.filter((h) => h.id !== open?.id);

  // Вывод выключен и заявок не было: показывать нечего, кроме причины.
  if (!state.enabled && !open && past.length === 0) {
    return (
      <div className="calm-card rounded-[24px] p-6 text-center space-y-3 flex flex-col items-center">
        <IconChip icon={Wallet} tone="neutral" size={48} />
        <h3 className="text-base font-bold text-foreground">{t('Withdraw unavailable title')}</h3>
        <p className="text-sm text-[#8a8c93] leading-snug">{t('Withdraw unavailable desc')}</p>
      </div>
    );
  }

  return (
    <>
      <MoneyCard state={state} />
      {open ? (
        <OpenRequestCard item={open} />
      ) : state.enabled ? (
        <WithdrawForm state={state} />
      ) : (
        <div className="calm-card rounded-[20px] p-4 text-sm text-[#8a8c93] leading-snug">
          {t('Withdraw unavailable desc')}
        </div>
      )}
      {past.length > 0 && (
        <div className="space-y-3">
          <SectionLabel>{t('Withdraw history')}</SectionLabel>
          {past.map((item) => (
            <HistoryRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </>
  );
}

function MoneyCard({ state }: { state: WithdrawState }) {
  const { t, i18n } = useTranslation();
  const money = (minor: number) => formatWholeMinor(minor, state.currency, i18n.language);
  return (
    <div className="calm-card rounded-[24px] p-5 grid grid-cols-2 gap-4">
      {/* Подпись слева переносится на вторую строку: числа прижаты вниз, чтобы стоять
          на одной линии. */}
      <div className="min-w-0 flex flex-col justify-between gap-1">
        <div className="text-[12px] text-[#8a8c93]">{t('Withdraw earned')}</div>
        <div className="text-[22px] font-extrabold tabular text-foreground truncate">
          {money(state.earned_display_minor)}
        </div>
      </div>
      <div className="min-w-0 flex flex-col justify-between gap-1">
        <div className="text-[12px] text-[#8a8c93]">{t('Withdraw available')}</div>
        <div className="text-[22px] font-extrabold tabular text-[#5fe3ad] truncate">
          {money(state.available_display_minor)}
        </div>
      </div>
      {/* Разница между двумя цифрами без объяснения читается как ошибка в расчёте. */}
      {state.available_display_minor < state.earned_display_minor && (
        <p className="col-span-2 -mt-1 text-[11px] text-[#6b6d74] leading-snug">
          {t('Withdraw available note')}
        </p>
      )}
    </div>
  );
}

// ─── Открытая заявка ────────────────────────────────────────────────────────
function OpenRequestCard({ item }: { item: WithdrawalItem }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const busyRef = useRef(false);
  const amount = formatWholeMinor(item.amount_display_minor, item.currency, i18n.language);
  // Владелец сервиса уже переводит деньги: отмены нет ни кнопкой, ни диалогом, который
  // успели открыть до того, как перезапрос принёс новый статус.
  const cancellable = canCancelWithdrawal(item.status);

  const cancel = useMutation({
    mutationFn: () => api.cancelReferralWithdrawal(item.id),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: (fresh) => {
      qc.setQueryData(WITHDRAW_QUERY_KEY, fresh);
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['payments-history'] });
      haptic('success');
      toast.success(t('Withdraw cancelled toast', { amount }));
    },
    onError: (e) => {
      haptic('error');
      const { code } = withdrawErrorOf(e);
      if (code === 'app.referral_withdraw.closed') toast.info(t('Withdraw already decided'));
      else if (code === 'app.referral_withdraw.processing') toast.info(t('Withdraw processing no cancel'));
      else if (!isServerFailure(e)) toast.error(t('Withdraw cancel failed'));
      // Партнёр мог успеть выплатить, отклонить или начать перевод: показываем, что сейчас.
      void qc.invalidateQueries({ queryKey: WITHDRAW_QUERY_KEY });
    },
  });

  const confirmCancel = () => {
    if (busyRef.current) return;
    busyRef.current = true;
    cancel.mutate();
  };

  return (
    <>
      <div className="calm-card rounded-[24px] p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <SectionLabel className="mb-1.5">{t('Withdraw request label')}</SectionLabel>
            <div className="text-[28px] font-extrabold tabular text-foreground leading-none">{amount}</div>
          </div>
          <StatusBadge status={item.status} />
        </div>
        <p className="text-sm text-[#c2c4ca] leading-snug">
          {item.payout_mode === 'platform'
            ? t('Withdraw platform note')
            : cancellable
              ? t('Withdraw waiting transfer')
              : t('Withdraw processing note')}
        </p>
        <dl className="space-y-2.5 text-sm">
          <DetailRow label={t('Withdraw where')} value={withdrawMethodLabel(item.method)} />
          <DetailRow
            label={t('Withdraw details')}
            value={shownDestination(item)}
            mono={item.method === 'usdt_trc20' || item.method === 'ton'}
          />
          {item.memo && <DetailRow label={t('Withdraw memo label')} value={item.memo} mono />}
          {isPlatformMethod(item.method) && item.net_display_minor != null && (
            <DetailRow
              label={t('Withdraw net label')}
              value={formatWholeMinor(item.net_display_minor, item.currency, i18n.language)}
            />
          )}
          <DetailRow label={t('Withdraw created')} value={shortDateTime(item.created_at, i18n.language)} />
        </dl>
        {cancellable && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={cancel.isPending}
            className="w-full py-3 rounded-xl bg-white/[0.06] text-sm font-bold text-[#f0656a] active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {cancel.isPending ? t('Withdraw cancelling') : t('Withdraw cancel')}
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirming && cancellable}
        onClose={() => setConfirming(false)}
        onConfirm={confirmCancel}
        title={t('Withdraw cancel title')}
        description={t('Withdraw cancel desc', { amount })}
        confirmLabel={t('Withdraw cancel')}
        cancelLabel={t('Withdraw keep')}
        destructive
      />
    </>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-[#8a8c93] shrink-0">{label}</dt>
      <dd
        className={cn('text-foreground font-semibold text-right min-w-0 break-all', mono && 'text-[13px]')}
        style={mono ? { fontFamily: 'var(--font-mono)' } : undefined}
      >
        {value}
      </dd>
    </div>
  );
}

// ─── Форма заявки ───────────────────────────────────────────────────────────
type FieldErrors = { amount?: string; destination?: string; bank?: string; memo?: string; form?: string };

function WithdrawForm({ state }: { state: WithdrawState }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const money = (minor: number) => formatWholeMinor(minor, state.currency, i18n.language);

  const availableWhole = Math.floor(state.available_display_minor / 100);
  const notEnough = state.available_display_minor < state.min_display_minor;
  const noMethods = state.methods.length === 0;
  const locked = notEnough || noMethods;

  // Сумма в целых рублях: копеек бэк не принимает. По умолчанию всё доступное.
  const [amountText, setAmountText] = useState(() => (notEnough ? '' : String(availableWhole)));
  const [picked, setPicked] = useState<WithdrawMethod | null>(null);
  // Реквизиты у каждого способа свои: переключился и вернулся — набранное на месте.
  const [card, setCard] = useState('');
  const [phone, setPhone] = useState('');
  const [bank, setBank] = useState('');
  const [wallet, setWallet] = useState('');
  const [tonAddress, setTonAddress] = useState('');
  const [memo, setMemo] = useState('');
  const [touched, setTouched] = useState<{ destination?: boolean; bank?: boolean }>({});
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});

  const method: WithdrawMethod | null =
    picked && state.methods.includes(picked) ? picked : (state.methods[0] ?? null);
  // У GRAM и CryptoBot свой минимум: меньше комиссия сети съела бы почти всю сумму.
  const methodMin = (method && state.method_min?.[method]) || state.min_display_minor;
  const minWhole = Math.ceil(methodMin / 100);

  // ── Проверки, повторяющие серверные ──
  const amountValue = /^\d+$/.test(amountText) ? Number(amountText) : null;
  const amountError =
    amountText === ''
      ? null
      : amountValue === null
        ? t('Withdraw amount whole')
        : amountValue < minWhole
          ? t('Withdraw amount min', { amount: money(methodMin) })
          : amountValue > availableWhole
            ? t('Withdraw amount max', { amount: money(state.available_display_minor) })
            : null;
  const amountOk = amountValue !== null && amountError === null;

  const normalizedPhone = normalizePhone(phone);
  const destinationOk =
    method === 'card'
      ? isValidCard(card)
      : method === 'sbp'
        ? normalizedPhone !== null
        : method === 'usdt_trc20'
          ? isValidTrc20(wallet)
          : method === 'ton'
            ? isValidTonAddress(tonAddress)
            : method === 'cryptobot';
  const memoOk = method !== 'ton' || [...normalizeMemo(memo)].length <= MEMO_MAX;
  const bankOk = method !== 'sbp' || isValidBank(bank);

  const destinationClientError = (() => {
    if (method === 'card') {
      // Номер проверяем, когда он набран целиком или поле покинули: на пятой цифре
      // «ошибка в номере» была бы враньём.
      if (!(touched.destination || card.length >= 16) || card === '' || isValidCard(card)) return null;
      return card.length < 16 ? t('Withdraw card short') : t('Withdraw card invalid');
    }
    if (method === 'sbp') {
      if (!touched.destination || phone.trim() === '' || normalizedPhone) return null;
      return t('Withdraw phone invalid');
    }
    if (method === 'usdt_trc20') {
      const value = wallet.trim();
      if (!(touched.destination || value.length >= 34) || value === '' || isValidTrc20(value)) return null;
      return t('Withdraw trc20 invalid');
    }
    if (method === 'ton') {
      const value = tonAddress.trim();
      if (!(touched.destination || value.length >= 48) || value === '' || isValidTonAddress(value)) return null;
      return t('Withdraw ton invalid');
    }
    return null;
  })();
  const bankClientError =
    method === 'sbp' && touched.bank && bank.trim() !== '' && !isValidBank(bank)
      ? t('Withdraw bank invalid', { max: BANK_MAX })
      : null;

  const canSubmit = !locked && method !== null && amountOk && destinationOk && bankOk && memoOk;

  // «На руки» при автоматической выплате: считает бэк той же формулой, что и заявку.
  // Запрос с задержкой, чтобы не дёргать сервер на каждую цифру.
  const quoteAmount = amountOk && amountValue !== null ? amountValue * 100 : null;
  const [debouncedAmount, setDebouncedAmount] = useState<number | null>(quoteAmount);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedAmount(quoteAmount), 350);
    return () => window.clearTimeout(timer);
  }, [quoteAmount]);
  // Только способы, которые сервис платит прямо сейчас: иначе переводит владелец, и
  // обещать «на руки» с комиссией сети было бы неправдой.
  const autoPaid = method !== null && (state.platform_methods ?? []).includes(method);
  const quoteOn = autoPaid && debouncedAmount !== null && !locked;
  const quote = useQuery<WithdrawQuote>({
    queryKey: ['referral-withdraw-quote', method, debouncedAmount],
    queryFn: () => api.referralWithdrawQuote(method as WithdrawMethod, debouncedAmount as number),
    enabled: quoteOn,
    staleTime: 60_000,
  });
  const quoteFresh =
    quoteOn && quote.data && quote.data.amount_display_minor === quoteAmount ? quote.data : null;

  /** Правило поля реквизитов: тот же текст и на своей проверке, и на отказе сервера. */
  const destinationRuleText = (m: WithdrawMethod | null) =>
    m === 'card'
      ? t('Withdraw card invalid')
      : m === 'sbp'
        ? t('Withdraw phone invalid')
        : m === 'ton'
          ? t('Withdraw ton invalid')
          : t('Withdraw trc20 invalid');

  const clearServer = (field: keyof FieldErrors) =>
    setServerErrors((prev) => (prev[field] || prev.form ? { ...prev, [field]: undefined, form: undefined } : prev));

  const busyRef = useRef(false);

  const create = useMutation({
    mutationFn: (body: CreateWithdrawalBody) => api.createReferralWithdrawal(body),
    onSettled: () => {
      busyRef.current = false;
    },
    onSuccess: (fresh, body) => {
      qc.setQueryData(WITHDRAW_QUERY_KEY, fresh);
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['payments-history'] });
      haptic('success');
      // Сервис мог выплатить сразу (авто-выплата владельца): тогда заявки нет среди
      // открытых, а «напишем, когда переведут» было бы уже неправдой.
      const paidNow = !fresh.open && fresh.history[0]?.status === 'paid';
      toast.success(
        t(paidNow ? 'Withdraw paid now toast' : 'Withdraw sent toast', {
          amount: money(body.amount_display_minor),
        }),
        { duration: 6000 },
      );
    },
    onError: (e) => {
      haptic('error');
      const { code, context } = withdrawErrorOf(e);
      const num = (v: unknown, fallback: number) => (typeof v === 'number' ? v : fallback);
      switch (code) {
        case 'app.referral_withdraw.amount': {
          if (context.reason === 'below_min') {
            setServerErrors({
              amount: t('Withdraw amount min', {
                amount: money(num(context.min_display_minor, state.min_display_minor)),
              }),
            });
          } else if (context.reason === 'above_available') {
            setServerErrors({
              amount: t('Withdraw amount max', {
                amount: money(num(context.available_display_minor, state.available_display_minor)),
              }),
            });
          } else {
            setServerErrors({ amount: t('Withdraw amount whole') });
          }
          // Доступное могло уменьшиться с момента загрузки экрана.
          void qc.invalidateQueries({ queryKey: WITHDRAW_QUERY_KEY });
          return;
        }
        case 'app.referral_withdraw.destination':
          if (context.field === 'bank') setServerErrors({ bank: t('Withdraw bank invalid', { max: BANK_MAX }) });
          else if (context.field === 'memo') setServerErrors({ memo: t('Withdraw memo invalid', { max: MEMO_MAX }) });
          else setServerErrors({ destination: destinationRuleText(method) });
          return;
        case 'app.referral_withdraw.method':
          setServerErrors({ form: t('Withdraw method gone') });
          void qc.invalidateQueries({ queryKey: WITHDRAW_QUERY_KEY });
          return;
        case 'app.referral_withdraw.disabled':
          // Перезапрос уберёт форму и покажет, что вывод выключен.
          toast.info(t('Withdraw unavailable desc'));
          void qc.invalidateQueries({ queryKey: WITHDRAW_QUERY_KEY });
          return;
        case 'app.referral_withdraw.open_exists':
          // Заявка ушла из бота или с соседней вкладки: перезапрос покажет её карточку.
          toast.info(t('Withdraw open exists'));
          void qc.invalidateQueries({ queryKey: WITHDRAW_QUERY_KEY });
          return;
        case 'app.referral_withdraw.too_frequent':
          // 429, лимит заявок в час. Форма заполнена верно, поэтому тост, а не ошибка
          // у поля: набранное остаётся, отправить можно будет позже.
          toast.error(t('Withdraw too frequent'));
          return;
        default:
          setServerErrors({ form: t('Withdraw send failed') });
      }
    },
  });

  // ── Кнопка Telegram ──
  const amountLabel = amountOk && amountValue !== null ? money(amountValue * 100) : '';
  const buttonText = canSubmit ? t('Withdraw submit amount', { amount: amountLabel }) : t('Withdraw submit');

  // Заявка в том виде, в каком уйдёт на сервер, или null, пока форма не готова.
  // Обработчик кнопки читает её из ref: иначе он перевешивался бы на каждый символ
  // в поле, и Telegram получал бы лишние команды настройки кнопки.
  const memoValue = method === 'ton' ? normalizeMemo(memo) : '';
  const draft: { body: CreateWithdrawalBody; where: string; net: string | null } | null =
    canSubmit && method !== null && amountValue !== null
      ? {
          body: {
            amount_display_minor: amountValue * 100,
            method,
            destination:
              method === 'card'
                ? card
                : method === 'sbp'
                  ? (normalizedPhone ?? phone.trim())
                  : method === 'ton'
                    ? tonAddress.trim()
                    : method === 'cryptobot'
                      ? ''
                      : wallet.trim(),
            ...(method === 'sbp' ? { bank: normalizeBank(bank) } : {}),
            ...(memoValue ? { memo: memoValue } : {}),
          },
          where:
            method === 'card'
              ? groupCard(card)
              : method === 'sbp'
                ? `${normalizedPhone ?? phone.trim()} · ${normalizeBank(bank)}`
                : method === 'ton'
                  ? memoValue
                    ? `${tonAddress.trim()} (memo ${memoValue})`
                    : tonAddress.trim()
                  : method === 'cryptobot'
                    ? t('Withdraw to telegram')
                    : wallet.trim(),
          net: quoteFresh ? money(quoteFresh.net_display_minor) : null,
        }
      : null;
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  });

  const { mutate: submit, isPending: submitting } = create;
  const { currency } = state;
  const lng = i18n.language;

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    // Без суммы или способов кнопке нечего отправлять: объяснение уже на экране.
    if (locked) {
      tg.MainButton.hide();
      return;
    }
    tg.MainButton.setParams({ text: buttonText, is_visible: true, is_active: canSubmit });
  }, [locked, buttonText, canSubmit]);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton || locked) return;
    const onMain = async () => {
      // Замок до первого await: попап подтверждения асинхронный, и второй тап за это
      // время отправил бы вторую заявку (бэк её отобьёт, но человек увидит ошибку).
      const current = draftRef.current;
      if (!current || busyRef.current) return;
      busyRef.current = true;
      hideKeyboard();
      // Партнёр переведёт деньги ровно на эти реквизиты: опечатку лучше поймать здесь.
      const message = t('Withdraw confirm message', {
        amount: formatWholeMinor(current.body.amount_display_minor, currency, lng),
        method: withdrawMethodLabel(current.body.method),
        destination: current.where,
      });
      const ok = await confirmAction(
        current.net ? `${message}\n\n${t('Withdraw confirm net', { net: current.net })}` : message,
        t('Withdraw confirm title'),
        { okText: t('Withdraw confirm ok'), destructive: false },
      );
      if (!ok) {
        busyRef.current = false;
        return;
      }
      submit(current.body);
    };
    tg.MainButton.onClick(onMain);
    return () => tg.MainButton.offClick(onMain);
  }, [locked, submit, currency, lng, t]);

  // Прогресс отдельным эффектом: showProgress() без аргумента сам блокирует кнопку.
  // hideProgress() в SDK Telegram кнопку сам же и включает, поэтому после него
  // активность ставим заново: иначе пустая форма открывалась с живой кнопкой.
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    if (submitting) {
      tg.MainButton.showProgress();
    } else {
      tg.MainButton.hideProgress();
      if (!locked) tg.MainButton.setParams({ is_active: canSubmit });
    }
  }, [submitting, locked, canSubmit]);

  // Форма уходит (заявка создана, вывод выключили) — кнопка уходит вместе с ней.
  useEffect(
    () => () => {
      const tg = getWebApp();
      tg?.MainButton.hideProgress();
      tg?.MainButton.hide();
    },
    [],
  );

  // Форма заблокирована (не хватает до минимума, нет способов): ошибки полей там
  // только шумят, причина уже написана сверху.
  const shownAmountError = locked ? null : (serverErrors.amount ?? amountError);
  const shownDestinationError = locked ? null : (serverErrors.destination ?? destinationClientError);
  const shownBankError = locked ? null : (serverErrors.bank ?? bankClientError);
  const memoClientError = memoOk ? null : t('Withdraw memo invalid', { max: MEMO_MAX });
  const shownMemoError = locked ? null : (serverErrors.memo ?? memoClientError);
  const sign = currencySign(state.currency);

  return (
    <div className="space-y-5">
      {notEnough && (
        <div className="rounded-[20px] p-4 border border-[rgba(240,176,78,0.25)] bg-[rgba(240,176,78,0.08)] space-y-1">
          <div className="text-sm font-bold text-foreground">
            {t('Withdraw not enough', {
              amount: money(state.min_display_minor - state.available_display_minor),
            })}
          </div>
          <div className="text-[12px] text-[#c2c4ca] leading-snug">
            {t('Withdraw not enough hint', { amount: money(state.min_display_minor) })}
          </div>
        </div>
      )}
      {noMethods && !notEnough && (
        <div className="calm-card rounded-[20px] p-4 text-sm text-[#8a8c93] leading-snug">
          {t('Withdraw no methods')}
        </div>
      )}

      <fieldset disabled={locked || submitting} className={cn('space-y-5 min-w-0', locked && 'opacity-50')}>
        <div>
          <SectionLabel className="mb-3">{t('Amount')}</SectionLabel>
          <div className="relative">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              aria-label={t('Amount')}
              aria-invalid={Boolean(shownAmountError)}
              value={amountText}
              onChange={(e) => {
                setAmountText(e.target.value.replace(/\D/g, '').slice(0, 9));
                clearServer('amount');
              }}
              placeholder={String(minWhole)}
              className={cn(
                'w-full calm-card rounded-xl pl-4 pr-10 py-3 text-[18px] font-bold tabular text-foreground focus:outline-none focus:ring-2 focus:ring-accent',
                shownAmountError && 'ring-1 ring-[#f0656a]/60',
              )}
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8a8c93] font-bold pointer-events-none">
              {sign}
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[12px] text-[#8a8c93]">
              {t('Withdraw min hint', { amount: money(methodMin) })}
            </span>
            {!notEnough && (
              <button
                type="button"
                onClick={() => {
                  setAmountText(String(availableWhole));
                  clearServer('amount');
                  haptic('light');
                }}
                className="shrink-0 rounded-full px-3 py-1.5 text-[12px] font-bold bg-white/[0.06] text-accent active:scale-[0.97] transition-transform"
              >
                {t('Withdraw all chip', { amount: money(state.available_display_minor) })}
              </button>
            )}
          </div>
          {shownAmountError && <FieldError>{shownAmountError}</FieldError>}
        </div>

        {!noMethods && (
          <div>
            <SectionLabel className="mb-3">{t('Withdraw where')}</SectionLabel>
            <div className="space-y-2">
              {state.methods.map((m) => (
                <OptionRow
                  key={m}
                  icon={METHOD_ICON[m] ?? Wallet}
                  iconTone="neutral"
                  title={withdrawMethodLabel(m)}
                  selected={method === m}
                  onSelect={() => {
                    setPicked(m);
                    setServerErrors({});
                    haptic('light');
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {method === 'card' && (
          <Field label={t('Withdraw card label')} hint={t('Withdraw card hint')} error={shownDestinationError}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              aria-label={t('Withdraw card label')}
              aria-invalid={Boolean(shownDestinationError)}
              value={groupCard(card)}
              onChange={(e) => {
                setCard(cardDigits(e.target.value));
                clearServer('destination');
              }}
              onBlur={() => setTouched((p) => ({ ...p, destination: true }))}
              placeholder="0000 0000 0000 0000"
              className={inputClass(Boolean(shownDestinationError), true)}
            />
          </Field>
        )}

        {method === 'sbp' && (
          <>
            <Field label={t('Withdraw phone label')} hint={t('Withdraw phone hint')} error={shownDestinationError}>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                aria-label={t('Withdraw phone label')}
                aria-invalid={Boolean(shownDestinationError)}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value.slice(0, 24));
                  clearServer('destination');
                }}
                onBlur={() => setTouched((p) => ({ ...p, destination: true }))}
                placeholder="+7 999 123-45-67"
                className={inputClass(Boolean(shownDestinationError), true)}
              />
            </Field>
            <Field label={t('Withdraw bank label')} hint={t('Withdraw bank hint')} error={shownBankError}>
              <input
                type="text"
                autoComplete="off"
                aria-label={t('Withdraw bank label')}
                aria-invalid={Boolean(shownBankError)}
                value={bank}
                maxLength={BANK_MAX + 10}
                onChange={(e) => {
                  setBank(e.target.value);
                  clearServer('bank');
                }}
                onBlur={() => setTouched((p) => ({ ...p, bank: true }))}
                placeholder={t('Withdraw bank placeholder')}
                className={inputClass(Boolean(shownBankError))}
              />
            </Field>
          </>
        )}

        {method === 'usdt_trc20' && (
          <Field label={t('Withdraw trc20 label')} hint={t('Withdraw trc20 hint')} error={shownDestinationError}>
            <input
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-label={t('Withdraw trc20 label')}
              aria-invalid={Boolean(shownDestinationError)}
              value={wallet}
              maxLength={64}
              onChange={(e) => {
                setWallet(e.target.value.replace(/\s/g, ''));
                clearServer('destination');
              }}
              onBlur={() => setTouched((p) => ({ ...p, destination: true }))}
              placeholder="T…"
              className={cn(inputClass(Boolean(shownDestinationError)), 'text-[13px]')}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
        )}

        {method === 'ton' && (
          <>
            <Field label={t('Withdraw ton label')} hint={t('Withdraw ton hint')} error={shownDestinationError}>
              <input
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-label={t('Withdraw ton label')}
                aria-invalid={Boolean(shownDestinationError)}
                value={tonAddress}
                maxLength={80}
                onChange={(e) => {
                  setTonAddress(e.target.value.replace(/\s/g, ''));
                  clearServer('destination');
                }}
                onBlur={() => setTouched((p) => ({ ...p, destination: true }))}
                placeholder="UQ…"
                className={cn(inputClass(Boolean(shownDestinationError)), 'text-[13px]')}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </Field>
            <Field label={t('Withdraw memo label')} hint={t('Withdraw memo hint')} error={shownMemoError}>
              <input
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-label={t('Withdraw memo label')}
                aria-invalid={Boolean(shownMemoError)}
                value={memo}
                maxLength={MEMO_MAX + 10}
                onChange={(e) => {
                  setMemo(e.target.value);
                  clearServer('memo');
                }}
                placeholder={t('Withdraw memo placeholder')}
                className={inputClass(Boolean(shownMemoError))}
              />
            </Field>
          </>
        )}

        {method === 'cryptobot' && (
          <div className="calm-card rounded-[20px] p-4 text-[13px] text-[#c2c4ca] leading-snug">
            {t('Withdraw cryptobot note')}
          </div>
        )}

        {quoteOn && (
          <div className="rounded-[20px] p-4 border border-white/[0.06] bg-white/[0.03] space-y-1">
            {quoteFresh ? (
              <>
                <div className="text-sm font-bold text-foreground tabular">
                  {t('Withdraw net line', { net: money(quoteFresh.net_display_minor) })}
                </div>
                <div className="text-[12px] text-[#8a8c93] leading-snug">
                  {t('Withdraw fee line', { fee: money(quoteFresh.fee_display_minor) })}
                </div>
              </>
            ) : quote.isError ? (
              <div className="text-[12px] text-[#8a8c93] leading-snug">{t('Withdraw quote failed')}</div>
            ) : (
              <SkeletonLine size="sm" className="w-1/2" />
            )}
          </div>
        )}
      </fieldset>

      {serverErrors.form && (
        <div className="rounded-[16px] p-3 text-[13px] text-[#f0656a] bg-[rgba(240,101,106,0.08)] leading-snug">
          {serverErrors.form}
        </div>
      )}
    </div>
  );
}

function inputClass(invalid: boolean, tabular = false): string {
  return cn(
    'w-full calm-card rounded-xl px-4 py-3 text-[15px] text-foreground focus:outline-none focus:ring-2 focus:ring-accent',
    tabular && 'tabular',
    invalid && 'ring-1 ring-[#f0656a]/60',
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint: string;
  error: string | null | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <SectionLabel className="mb-3">{label}</SectionLabel>
      {children}
      {error ? <FieldError>{error}</FieldError> : <p className="mt-2 text-[12px] text-[#8a8c93] leading-snug">{hint}</p>}
    </div>
  );
}

function FieldError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mt-2 text-[12px] text-[#f0656a] leading-snug">
      {children}
    </p>
  );
}

// ─── История ────────────────────────────────────────────────────────────────
const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  pending: { bg: 'rgba(240,176,78,0.12)', fg: '#f0b04e' },
  // Не акцент бренда: у партнёра он может быть любым, вплоть до красного.
  processing: { bg: 'rgba(90,160,255,0.12)', fg: '#5aa0ff' },
  paid: { bg: 'rgba(56,224,165,0.12)', fg: '#5fe3ad' },
  rejected: { bg: 'rgba(240,101,106,0.12)', fg: '#f0656a' },
  cancelled: { bg: 'rgba(255,255,255,0.07)', fg: '#8a8c93' },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.cancelled;
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11px] font-bold"
      style={{ background: style.bg, color: style.fg }}
    >
      {withdrawStatusLabel(status)}
    </span>
  );
}

function HistoryRow({ item }: { item: WithdrawalItem }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="calm-card rounded-[20px] p-4 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="font-bold tabular text-foreground">
          {formatWholeMinor(item.amount_display_minor, item.currency, i18n.language)}
        </div>
        <StatusBadge status={item.status} />
      </div>
      <div className="text-[12px] text-[#8a8c93] truncate">
        {withdrawMethodLabel(item.method)} · {shownDestination(item)}
      </div>
      <div className="text-[11px] text-[#6b6d74]">
        {shortDateTime(item.decided_at ?? item.created_at, i18n.language)}
      </div>
      {item.status === 'rejected' && item.reject_reason && (
        <div className="pt-1 text-[12px] text-[#f0656a] leading-snug break-words">
          {t('Withdraw reject reason', { reason: item.reject_reason })}
        </div>
      )}
      {item.status === 'paid' && (item.check_url || item.tx_hash) && (
        <button
          type="button"
          onClick={() => openExternalUrl(item.check_url ?? `https://tonviewer.com/transaction/${item.tx_hash}`)}
          className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-bold text-accent active:opacity-70"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {item.check_url ? t('Withdraw open check') : t('Withdraw open tx')}
        </button>
      )}
    </div>
  );
}

/**
 * Повторяет две верхние карточки: суммы в две колонки и форму (способы и поле).
 * Заголовок экрана настоящий, он от данных не зависит.
 */
function ReferralWithdrawSkeleton() {
  return (
    <div className="space-y-5">
      <SkeletonCard className="rounded-[24px] min-h-0 p-5 grid grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2">
            <SkeletonLine size="sm" className="w-3/4" />
            <SkeletonLine size="hero" className="w-2/3" />
          </div>
        ))}
      </SkeletonCard>
      <div className="space-y-3">
        <SkeletonLine size="xs" className="w-16" />
        <Skeleton className="h-[52px] w-full rounded-xl" />
        <SkeletonLine size="sm" className="w-1/3" />
      </div>
      <div className="space-y-2">
        <SkeletonLine size="xs" className="w-20" />
        <SkeletonCard className="h-[66px] min-h-0 rounded-[16px]" />
        <SkeletonCard className="h-[66px] min-h-0 rounded-[16px]" />
      </div>
    </div>
  );
}
